import React, { useState } from 'react';
import { authFetch } from '../services/api';

export default function ReferenceDesignModal({ projectId, onClose, onStarted }) {
  const [mode, setMode] = useState('screenshot'); // 'screenshot' | 'url' | 'figma'
  const [prompt, setPrompt] = useState('');
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [refUrl, setRefUrl] = useState('');
  // FIGMA-REFERENCE-FEATURE: pasting a Figma link into the "Reference URL"
  // tab above doesn't work — Figma's edge blocks headless-browser
  // navigation to any real design page outright (confirmed live), the
  // same bot-protection class that blocks stripe.com. Figma's own REST
  // API renders the frame instead, authenticated with a Personal Access
  // Token the user pastes in per-request — never persisted server-side,
  // matching git_push.py's existing token-handling convention.
  const [figmaUrl, setFigmaUrl] = useState('');
  const [figmaToken, setFigmaToken] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setPreviewUrl(URL.createObjectURL(f));
    setError('');
  };

  const canSubmit = prompt.trim() && !submitting && (
    mode === 'screenshot' ? !!file :
    mode === 'figma' ? (figmaUrl.trim() && figmaToken.trim()) :
    refUrl.trim()
  );

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError('');
    try {
      let res;
      if (mode === 'screenshot') {
        const formData = new FormData();
        formData.append('prompt', prompt.trim());
        formData.append('file', file);
        res = await authFetch(`/api/projects/${projectId}/generate-from-screenshot`, {
          method: 'POST',
          body: formData,
        });
      } else if (mode === 'figma') {
        res = await authFetch(`/api/projects/${projectId}/generate-from-figma`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ figma_url: figmaUrl.trim(), figma_token: figmaToken.trim(), prompt: prompt.trim() }),
        });
      } else {
        res = await authFetch(`/api/projects/${projectId}/generate-from-url`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ url: refUrl.trim(), prompt: prompt.trim() }),
        });
      }
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Request failed (${res.status})`);
      }
      const data = await res.json();
      onStarted(data.job_id);
    } catch (e) {
      setError(e.message || 'Something went wrong');
      setSubmitting(false);
    }
  };

  return (
    <div style={s.overlay} onClick={onClose}>
      <div style={s.modal} onClick={e => e.stopPropagation()}>
        <div style={s.header}>
          <h3 style={s.title}>Build from a design reference</h3>
          <button style={s.closeBtn} onClick={onClose}>✕</button>
        </div>

        <div style={s.tabs}>
          <button
            style={{ ...s.tab, ...(mode === 'screenshot' ? s.tabActive : {}) }}
            onClick={() => setMode('screenshot')}
          >📷 Upload Screenshot</button>
          <button
            style={{ ...s.tab, ...(mode === 'url' ? s.tabActive : {}) }}
            onClick={() => setMode('url')}
          >🔗 Reference URL</button>
          <button
            style={{ ...s.tab, ...(mode === 'figma' ? s.tabActive : {}) }}
            onClick={() => setMode('figma')}
          >🎨 Figma</button>
        </div>

        {mode === 'screenshot' ? (
          <div style={s.field}>
            <label style={s.label}>Screenshot (.png, .jpg, .webp — max 8MB)</label>
            <input type="file" accept=".png,.jpg,.jpeg,.webp" onChange={handleFileChange} style={s.fileInput} />
            {previewUrl && (
              <img src={previewUrl} alt="preview" style={s.preview} />
            )}
          </div>
        ) : mode === 'figma' ? (
          <>
            <div style={s.field}>
              <label style={s.label}>Figma frame link</label>
              <input
                type="text"
                value={figmaUrl}
                onChange={e => setFigmaUrl(e.target.value)}
                placeholder="https://www.figma.com/design/..."
                style={s.textInput}
              />
              <p style={s.hint}>Right-click the frame in Figma → "Copy link to selection" for the most precise result.</p>
            </div>
            <div style={s.field}>
              <label style={s.label}>Figma Personal Access Token</label>
              <input
                type="password"
                value={figmaToken}
                onChange={e => setFigmaToken(e.target.value)}
                placeholder="figd_…"
                style={s.textInput}
              />
              <p style={s.hint}>
                Used once for this request, never saved. Generate one at{' '}
                <a href="https://www.figma.com/developers/api#access-tokens" target="_blank" rel="noopener noreferrer" style={s.link}>
                  figma.com/developers/api#access-tokens
                </a>.
              </p>
            </div>
          </>
        ) : (
          <div style={s.field}>
            <label style={s.label}>URL of a site whose look you want to match</label>
            <input
              type="text"
              value={refUrl}
              onChange={e => setRefUrl(e.target.value)}
              placeholder="https://example.com"
              style={s.textInput}
            />
            <p style={s.hint}>We'll render the page and analyze its design — this takes a few seconds before generation starts.</p>
          </div>
        )}

        <div style={s.field}>
          <label style={s.label}>What do you want to build?</label>
          <textarea
            value={prompt}
            onChange={e => setPrompt(e.target.value)}
            placeholder="e.g. Build a company website for a logistics startup"
            style={s.textarea}
            rows={3}
          />
        </div>

        {error && <div style={s.error}>{error}</div>}

        <div style={s.actions}>
          <button style={s.cancelBtn} onClick={onClose} disabled={submitting}>Cancel</button>
          <button style={{ ...s.submitBtn, ...(canSubmit ? {} : s.submitBtnDisabled) }} onClick={handleSubmit} disabled={!canSubmit}>
            {submitting ? 'Starting…' : 'Generate'}
          </button>
        </div>
      </div>
    </div>
  );
}

const s = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal: { background: 'var(--bg-primary)', border: '1px solid var(--border)', borderRadius: 12, padding: 20, width: 440, maxWidth: '90vw', display: 'flex', flexDirection: 'column', gap: 14 },
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  title: { fontSize: 15, fontWeight: 700, color: 'var(--text-primary)', margin: 0 },
  closeBtn: { background: 'transparent', border: 'none', color: 'var(--text-muted)', fontSize: 16, cursor: 'pointer', padding: 4 },
  tabs: { display: 'flex', gap: 6, background: 'var(--bg-card)', padding: 4, borderRadius: 8 },
  tab: { flex: 1, padding: '8px 10px', background: 'transparent', border: 'none', borderRadius: 6, color: 'var(--text-secondary)', fontSize: 12, fontWeight: 600, cursor: 'pointer' },
  tabActive: { background: '#6366f1', color: '#fff' },
  field: { display: 'flex', flexDirection: 'column', gap: 6 },
  label: { fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' },
  fileInput: { fontSize: 12, color: 'var(--text-primary)' },
  preview: { maxWidth: '100%', maxHeight: 160, borderRadius: 8, border: '1px solid var(--border)', objectFit: 'cover' },
  textInput: { padding: '10px 12px', background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text-primary)', fontSize: 13, outline: 'none' },
  textarea: { padding: '10px 12px', background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text-primary)', fontSize: 13, outline: 'none', resize: 'vertical', fontFamily: 'inherit' },
  hint: { fontSize: 11, color: 'var(--text-muted)', margin: 0 },
  link: { color: '#818cf8' },
  error: { fontSize: 12, color: '#ef4444', background: 'rgba(239,68,68,0.1)', padding: '8px 10px', borderRadius: 6 },
  actions: { display: 'flex', gap: 8, justifyContent: 'flex-end' },
  cancelBtn: { padding: '8px 16px', background: 'transparent', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text-secondary)', fontSize: 13, cursor: 'pointer' },
  submitBtn: { padding: '8px 16px', background: '#6366f1', border: 'none', borderRadius: 8, color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  submitBtnDisabled: { opacity: 0.5, cursor: 'not-allowed' },
};
